import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type {
  AnalyticsQuery,
  AuditLogResponse,
  CreateCommentInput,
  CreateTaskInput,
  DashboardResponse,
  DeveloperAnalyticsResponse,
  NotificationLogResponse,
  ReferenceDataResponse,
  ReferenceItemInput,
  ReviewQueueResponse,
  RoleMappingInput,
  RoleMappingRecord,
  SettingsResponse,
  TaskDetail,
  TaskListResponse,
  TaskSummary,
  TeamAnalyticsResponse,
  TransitionTaskInput,
  UpdateTaskInput,
  UpdateUserInput,
  UserDetailResponse,
  UserListResponse,
  CreateTicketInput,
  CreateTicketTaskInput,
  TicketDetail,
  TicketDetailResponse,
  TicketListResponse,
} from '@ssn/shared';
import { api, toQueryString } from './api';
import { SESSION_QUERY_KEY } from '@/session/SessionProvider';

/**
 * Query keys.
 *
 * Grouped by resource so a mutation can invalidate a whole family at once —
 * after a transition, every list, the dashboard and the review queue are all
 * potentially stale, and getting that wrong shows people numbers that no longer
 * match what they just did.
 */
export const keys = {
  dashboard: ['dashboard'] as const,
  tasks: ['tasks'] as const,
  taskList: (query: Record<string, unknown>) => ['tasks', 'list', query] as const,
  task: (reference: number) => ['tasks', 'detail', reference] as const,
  reviewQueue: (query: Record<string, unknown>) => ['review', 'queue', query] as const,
  reference: ['reference'] as const,
  users: ['users'] as const,
  user: (id: string) => ['users', id] as const,
  teamAnalytics: (query: Record<string, unknown>) => ['analytics', 'team', query] as const,
  developerAnalytics: (id: string, query: Record<string, unknown>) =>
    ['analytics', 'developer', id, query] as const,
  audit: (query: Record<string, unknown>) => ['audit', query] as const,
  auditVerify: ['audit', 'verify'] as const,
  settings: ['settings'] as const,
  notifications: (query: Record<string, unknown>) => ['notifications', query] as const,
  tickets: ['tickets'] as const,
  ticketList: (query: Record<string, unknown>) => ['tickets', 'list', query] as const,
  ticket: (reference: number) => ['tickets', 'detail', reference] as const,
};

/* ---- Reads --------------------------------------------------------------- */

export function useDashboard() {
  return useQuery({
    queryKey: keys.dashboard,
    queryFn: ({ signal }) => api.get<DashboardResponse>('/api/dashboard', signal),
    staleTime: 15_000,
  });
}

export function useTaskList(query: Record<string, unknown>, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: keys.taskList(query),
    queryFn: ({ signal }) => api.get<TaskListResponse>(`/api/tasks${toQueryString(query)}`, signal),
    // Keeping the previous page on screen while the next one loads stops the
    // table collapsing to an empty state on every filter change.
    placeholderData: (previous) => previous,
    staleTime: 10_000,
    enabled: options?.enabled ?? true,
  });
}

export function useTask(reference: number, options?: Partial<UseQueryOptions<{ task: TaskDetail }>>) {
  return useQuery({
    queryKey: keys.task(reference),
    queryFn: ({ signal }) => api.get<{ task: TaskDetail }>(`/api/tasks/${reference}`, signal),
    staleTime: 5_000,
    ...options,
  });
}

export function useReviewQueue(query: Record<string, unknown>, enabled = true) {
  return useQuery({
    queryKey: keys.reviewQueue(query),
    queryFn: ({ signal }) => api.get<ReviewQueueResponse>(`/api/review/queue${toQueryString(query)}`, signal),
    placeholderData: (previous) => previous,
    staleTime: 10_000,
    enabled,
  });
}

export function useReferenceData(enabled = true) {
  return useQuery({
    queryKey: keys.reference,
    queryFn: ({ signal }) => api.get<ReferenceDataResponse>('/api/reference', signal),
    // Projects, categories and people change rarely; refetching them on every
    // form open would be pure noise.
    staleTime: 5 * 60_000,
    enabled,
  });
}

export function useUsers(enabled = true) {
  return useQuery({
    queryKey: keys.users,
    queryFn: ({ signal }) => api.get<UserListResponse>('/api/users', signal),
    staleTime: 30_000,
    enabled,
  });
}

export function useUser(userId: string, enabled = true) {
  return useQuery({
    queryKey: keys.user(userId),
    queryFn: ({ signal }) => api.get<UserDetailResponse>(`/api/users/${userId}`, signal),
    staleTime: 30_000,
    enabled,
  });
}

export function useTeamAnalytics(query: AnalyticsQuery | Record<string, unknown>, enabled = true) {
  return useQuery({
    queryKey: keys.teamAnalytics(query as Record<string, unknown>),
    queryFn: ({ signal }) =>
      api.get<TeamAnalyticsResponse>(`/api/analytics/team${toQueryString(query as Record<string, unknown>)}`, signal),
    placeholderData: (previous) => previous,
    staleTime: 60_000,
    enabled,
  });
}

export function useDeveloperAnalytics(
  userId: string,
  query: Record<string, unknown>,
  enabled = true,
) {
  return useQuery({
    queryKey: keys.developerAnalytics(userId, query),
    queryFn: ({ signal }) =>
      api.get<DeveloperAnalyticsResponse>(
        `/api/analytics/developers/${userId}${toQueryString(query)}`,
        signal,
      ),
    placeholderData: (previous) => previous,
    staleTime: 60_000,
    enabled,
  });
}

export function useAuditLog(query: Record<string, unknown>, enabled = true) {
  return useQuery({
    queryKey: keys.audit(query),
    queryFn: ({ signal }) => api.get<AuditLogResponse>(`/api/audit${toQueryString(query)}`, signal),
    placeholderData: (previous) => previous,
    staleTime: 15_000,
    enabled,
  });
}

export function useSettings(enabled = true) {
  return useQuery({
    queryKey: keys.settings,
    queryFn: ({ signal }) => api.get<SettingsResponse>('/api/settings', signal),
    staleTime: 30_000,
    enabled,
  });
}

export function useNotificationLog(query: Record<string, unknown>, enabled = true) {
  return useQuery({
    queryKey: keys.notifications(query),
    queryFn: ({ signal }) =>
      api.get<NotificationLogResponse>(`/api/notifications${toQueryString(query)}`, signal),
    placeholderData: (previous) => previous,
    staleTime: 15_000,
    enabled,
  });
}

/* ---- Mutations ----------------------------------------------------------- */

/** Everything a task mutation can invalidate. */
function useTaskInvalidation() {
  const client = useQueryClient();
  return async (reference?: number) => {
    await Promise.all([
      client.invalidateQueries({ queryKey: keys.tasks }),
      client.invalidateQueries({ queryKey: keys.dashboard }),
      client.invalidateQueries({ queryKey: ['review'] }),
      client.invalidateQueries({ queryKey: ['analytics'] }),
      client.invalidateQueries({ queryKey: keys.users }),
      reference ? client.invalidateQueries({ queryKey: keys.task(reference) }) : Promise.resolve(),
    ]);
  };
}

export function useCreateTask() {
  const invalidate = useTaskInvalidation();
  return useMutation({
    mutationFn: (input: CreateTaskInput) => api.post<{ task: TaskDetail }>('/api/tasks', input),
    onSuccess: async (result) => invalidate(result.task.reference),
  });
}

export function useUpdateTask(reference: number) {
  const invalidate = useTaskInvalidation();
  return useMutation({
    mutationFn: (input: UpdateTaskInput) =>
      api.patch<{ task: TaskDetail }>(`/api/tasks/${reference}`, input),
    onSuccess: async () => invalidate(reference),
  });
}

export function useTransitionTask(reference: number) {
  const invalidate = useTaskInvalidation();
  return useMutation({
    mutationFn: (input: TransitionTaskInput) =>
      api.post<{ task: TaskDetail }>(`/api/tasks/${reference}/transitions`, input),
    onSuccess: async () => invalidate(reference),
  });
}

export function useAssignTask(reference: number) {
  const invalidate = useTaskInvalidation();
  return useMutation({
    mutationFn: (input: { assigneeId: string | null; note?: string | null }) =>
      api.post<{ task: TaskDetail }>(`/api/tasks/${reference}/assignee`, input),
    onSuccess: async () => invalidate(reference),
  });
}

export function useDeleteTask(reference: number) {
  const invalidate = useTaskInvalidation();
  return useMutation({
    mutationFn: (input: { reason: string }) =>
      api.delete<{ task: TaskSummary }>(`/api/tasks/${reference}`, input),
    onSuccess: async () => invalidate(reference),
  });
}

export function useRestoreTask(reference: number) {
  const invalidate = useTaskInvalidation();
  return useMutation({
    mutationFn: (input: { reason?: string | null }) =>
      api.post<{ task: TaskSummary }>(`/api/tasks/${reference}/restore`, input),
    onSuccess: async () => invalidate(reference),
  });
}

export function useAddComment(reference: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCommentInput) =>
      api.post(`/api/tasks/${reference}/comments`, input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.task(reference) });
    },
  });
}

export function useEditComment(reference: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, body }: { commentId: string; body: string }) =>
      api.patch(`/api/comments/${commentId}`, { body }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.task(reference) });
    },
  });
}

export function useDeleteComment(reference: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => api.delete(`/api/comments/${commentId}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.task(reference) });
    },
  });
}

export function useUploadAttachment(reference: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.upload(`/api/tasks/${reference}/attachments`, form);
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.task(reference) });
    },
  });
}

export function useDeleteAttachment(reference: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => api.delete(`/api/attachments/${attachmentId}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.task(reference) });
    },
  });
}

export function useUpdateUser(userId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateUserInput) => api.patch<UserDetailResponse>(`/api/users/${userId}`, input),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: keys.users }),
        client.invalidateQueries({ queryKey: keys.user(userId) }),
        client.invalidateQueries({ queryKey: SESSION_QUERY_KEY }),
      ]);
    },
  });
}

export function useUpdateSetting() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      api.put<SettingsResponse>(`/api/settings/${encodeURIComponent(key)}`, { value }),
    onSuccess: async () => {
      // A setting can change what the whole UI is allowed to show, so the
      // session (which carries the feature flags) is refreshed too.
      await Promise.all([
        client.invalidateQueries({ queryKey: keys.settings }),
        client.invalidateQueries({ queryKey: SESSION_QUERY_KEY }),
      ]);
    },
  });
}

export function useUpsertRoleMapping() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: RoleMappingInput) =>
      api.put<{ roleMappings: RoleMappingRecord[] }>('/api/settings/role-mappings', input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.settings });
    },
  });
}

export function useRemoveRoleMapping() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (discordRoleId: string) =>
      api.delete<{ roleMappings: RoleMappingRecord[] }>(
        `/api/settings/role-mappings/${encodeURIComponent(discordRoleId)}`,
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.settings });
    },
  });
}

export function useSaveReferenceItem(table: 'projects' | 'categories') {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: ReferenceItemInput }) =>
      id
        ? api.patch<ReferenceDataResponse>(`/api/reference/${table}/${id}`, input)
        : api.post<ReferenceDataResponse>(`/api/reference/${table}`, input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.reference });
    },
  });
}

export function useDeactivateReferenceItem(table: 'projects' | 'categories') {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<ReferenceDataResponse>(`/api/reference/${table}/${id}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.reference });
    },
  });
}

/* ---------------------------------------------------------------------------
 * Leadership Requests ("tickets")
 * ------------------------------------------------------------------------- */

export function useTicketList(query: Record<string, unknown>) {
  return useQuery({
    queryKey: keys.ticketList(query),
    queryFn: ({ signal }) => api.get<TicketListResponse>(`/api/tickets${toQueryString(query)}`, signal),
    placeholderData: (previous) => previous,
    staleTime: 10_000,
  });
}

export function useTicket(reference: number, options?: Partial<UseQueryOptions<TicketDetailResponse>>) {
  return useQuery({
    queryKey: keys.ticket(reference),
    queryFn: ({ signal }) => api.get<TicketDetailResponse>(`/api/tickets/${reference}`, signal),
    staleTime: 5_000,
    ...options,
  });
}

function useTicketInvalidation() {
  const client = useQueryClient();
  return async (reference?: number) => {
    await Promise.all([
      client.invalidateQueries({ queryKey: keys.tickets }),
      reference ? client.invalidateQueries({ queryKey: keys.ticket(reference) }) : Promise.resolve(),
    ]);
  };
}

export function useCreateTicket() {
  const invalidate = useTicketInvalidation();
  return useMutation({
    mutationFn: (input: CreateTicketInput) => api.post<TicketDetailResponse>('/api/tickets', input),
    onSuccess: async (result) => invalidate(result.ticket.reference),
  });
}

export function useAssignTicket(reference: number) {
  const invalidate = useTicketInvalidation();
  return useMutation({
    mutationFn: (assigneeId: string) =>
      api.post<TicketDetailResponse>(`/api/tickets/${reference}/assign`, { assigneeId }),
    onSuccess: async () => invalidate(reference),
  });
}

export function useCompleteTicket(reference: number) {
  const invalidate = useTicketInvalidation();
  return useMutation({
    mutationFn: (comment: string | null) =>
      api.post<TicketDetailResponse>(`/api/tickets/${reference}/complete`, { comment }),
    onSuccess: async () => invalidate(reference),
  });
}

export function useDenyTicket(reference: number) {
  const invalidate = useTicketInvalidation();
  return useMutation({
    mutationFn: (reason: string) => api.post<TicketDetailResponse>(`/api/tickets/${reference}/deny`, { reason }),
    onSuccess: async () => invalidate(reference),
  });
}

/** The quick action: create a task from a claimed ticket, same form as the main interface. */
export function useCreateTaskFromTicket(reference: number) {
  const invalidate = useTicketInvalidation();
  return useMutation({
    mutationFn: (input: CreateTicketTaskInput) =>
      api.post<{ task: TaskDetail }>(`/api/tickets/${reference}/tasks`, input),
    onSuccess: async () => invalidate(reference),
  });
}

export function useAddTicketComment(reference: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCommentInput) => api.post(`/api/tickets/${reference}/comments`, input),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.ticket(reference) });
    },
  });
}

export function useEditTicketComment(reference: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, body }: { commentId: string; body: string }) =>
      api.patch(`/api/ticket-comments/${commentId}`, { body }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.ticket(reference) });
    },
  });
}

export function useDeleteTicketComment(reference: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => api.delete(`/api/ticket-comments/${commentId}`),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.ticket(reference) });
    },
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/auth/logout'),
    onSuccess: () => {
      client.clear();
    },
  });
}
